<template>
    <div class="card p-2 create-stacking-context">
        <div class="d-flex justify-content-center align-items-center flex-column gap-2">
            <div class="font-weight-bold p-1">
                {{ label }}
            </div>
            <font-awesome-icon
                v-if="!rendered"
                icon="circle-notch"
                spin />
            <div
                v-show="rendered"
                id="paypal-button-container"
                :class="{'paypal-disabled': disabled}" />
        </div>
    </div>
</template>

<script>
import {loadScript} from "@paypal/paypal-js";
import debounce from "lodash/debounce";

export default {
    name: "PaypalButton",
    props: {
        eventId: {
            type: Number,
            required: true
        },
        paymentTypeId: {
            type: Number,
            required: true
        },
        clientId: {
            type: String,
            required: true
        },
        priceToPay: {
            type: Number,
            required: true
        },
        couponTotal: {
            type: Number
        },
        articlesGrouped: {
            type: [Object, Array],
            required: true
        },
        currency: {
            type: String,
            default: "EUR"
        },
        label: {
            type: String,
            default: "Jetzt buchen"
        },
        disabled: {
            type: Boolean
        }
    },
    data() {
        return {
            paypal: null,
            actions: null,
            rendered: false,
            rendering: false,
            loadPaypalButtonsDebounce: debounce(() => this.loadPaypalButtons(), 500)
        }
    },
    computed: {
        validCurrency() {
            if (!this.currency) return ""
            switch (this.currency) {
                case "SFR":
                case "CHF":
                    return "CHF"
                case "EUR":
                case "USD":
                    return this.currency
                default:
                    return ""
            }
        }
    },
    methods: {
        onStartApprovingOrder() {
            this.$emit('authorizing')
        },
        onApprovedOrder(orderId, authorizationId, authorizationAmount) {
            this.$emit('authorized', {orderId, authorizationId, authorizationAmount})
        },
        onUnapprovedOrder() {
            this.$emit('unauthorized')
        },
        onOrderCreationError(error) {
            console.log(error)

            if (error.response?.status === 429) {
                this.$store.commit('registration/setTooManyAttempts', this.$moment().toISOString())
                this.$notify({
                    type: 'warning',
                    message: this.$t('error.tooManyRequests')
                })
                return
            }

            const message = JSON.parse(error.response?.data?.message)
            if (message) {
                this.$notify({
                    type: "danger",
                    message: this.$t("error." + message.message)
                })
            }
        },
        async loadPaypalButtons() {
            if (this.rendering) return
            this.rendering = true
            if (!this.paypal) {
                try {
                    if (!this.clientId) {
                        this.rendering = false
                        return
                    }
                    this.paypal = await loadScript({
                        "client-id": this.clientId,
                        "intent": "authorize", // 'capture', 'subscription'
                        "currency": this.validCurrency,
                        "locale": "de_DE",
                        "disable-funding": ["venmo", "sepa", "paylater", "bancontact"]
                    });
                } catch (error) {
                    console.error("failed to load the PayPal JS SDK script", error);
                }
            }
            if (this.paypal && document.querySelector("#paypal-button-container")) {
                this.$nextTick(() => {
                    const self = this
                    this.paypal.Buttons(
                        {
                            style: {
                                layout: 'horizontal',
                                label: 'pay',
                                tagline: false
                            },
                            commit: true,
                            onInit: function (data, actions) {
                                self.actions = actions
                                if (self.disabled)
                                    actions.disable();
                            },
                            // Sets up the transaction when a payment button is clicked
                            createOrder: function (data, actions) {
                                return self.$axios.post(
                                    self.$apiRoute(
                                        'api.registration.veranstaltung.registration.createOrder',
                                        {veranstaltung: self.eventId}),
                                    {
                                        paymentTypeId: self.paymentTypeId,
                                        total: self.priceToPay,
                                        articlesGrouped: self.articlesGrouped,
                                        discount: self.couponTotal
                                    }
                                ).then(function (response) {
                                    return response.data.orderId;
                                }).catch((error) => {
                                    self.onOrderCreationError(error)
                                    return null
                                });
                            },
                            // Finalize the transaction after payer approval
                            onApprove: function (data, actions) {
                                self.onStartApprovingOrder()
                                actions.order.authorize().then(function (authorization) {
                                    const authorizationID = authorization.purchase_units[0].payments.authorizations[0].id
                                    const authorizationAmount = authorization.purchase_units[0].payments.authorizations[0].amount?.value
                                    self.onApprovedOrder(data.orderID, authorizationID, authorizationAmount)
                                }).catch((e) => {
                                    self.onUnapprovedOrder()
                                });
                            }
                        }
                    ).render("#paypal-button-container")
                        .then(() => this.rendered = true)
                        .catch((error) => console.error("failed to render the PayPal Buttons", error))
                        .finally(() => this.rendering = false)
                })
            } else {
                this.rendering = false
            }
        }
    },
    mounted() {
        // debounce because of keep alive issue...
        // if payment type changes, this component seems to be loaded despite inactive parent
        // and then the container div is not ready
        this.loadPaypalButtonsDebounce()
    },
    watch: {
        disabled: {
            handler() {
                if (this.actions) {
                    if (this.disabled) {
                        this.actions.disable()
                    } else {
                        this.actions.enable()
                    }
                }
            },
            immediate: true
        }
    }
}
</script>

<style lang="scss" scoped>
#paypal-button-container {
    width: 250px;
    height: 40px;
}

.paypal-disabled {
    position: relative;
}

.paypal-disabled::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 101;
    width: 100%;
    height: 100%;
    background-color: rgba(white, 0.5);
}

.create-stacking-context {
    min-width: 250px;
    position: relative;
    z-index: 0
}
</style>
